import {
  IconBrain,
  IconTool,
  IconTypography,
  IconWorldSearch,
} from '@tabler/icons-react';
import { UIMessagePart } from 'ai';

import Markdown from '../../../Markdown';
import { getReasoningTitle, partIsTool } from '../utils';

import CollapsibleContent from './CollapsibleContent';
import ToolSummary from './summaries/ToolSummary';

import {
  PathwayUIDataParts,
  PathwayUITools,
} from '@/routes/smart-search/types';

interface StepSummaryProps {
  summary: string;
  stepParts: UIMessagePart<PathwayUIDataParts, PathwayUITools>[];
}

const StepSummary: React.FC<StepSummaryProps> = ({ summary, stepParts }) => (
  <CollapsibleContent title={summary}>
    <div className="flex flex-col py-2">
      {stepParts
        .filter(part => !part.type.startsWith('data'))
        .map((part, i) => (
          <RoadMapStep
            key={i}
            idx={i}
            length={stepParts.length}
            partType={part.type}
          >
            {part.type === 'reasoning' && (
              <CollapsibleContent title={getReasoningTitle(part.text)}>
                <Markdown text={part.text} className="text-sm text-gray-600" />
              </CollapsibleContent>
            )}
            {partIsTool(part) && <ToolSummary part={part} />}
          </RoadMapStep>
        ))}
    </div>
  </CollapsibleContent>
);

const partIcons = new Map<string, React.ReactNode>([
  ['reasoning', <IconBrain size={16} key="reasoning" />],
  ['text', <IconTypography size={16} key="text" />],
  ['tool', <IconTool size={16} key="tool" />],
  ['tool-search', <IconWorldSearch size={16} key="tool-search" />],
]);

const getPartIcon = (partType: string) => {
  if (partIcons.has(partType)) {
    return <div className="mt-[3px]">{partIcons.get(partType)}</div>;
  }
  if (partType.startsWith('tool')) {
    return <div className="mt-[3px]">{partIcons.get('tool')}</div>;
  }
  return (
    <div className="mt-[3px] size-3.5 shrink-0 rounded-full border-2 border-gray-300 bg-white" />
  );
};

interface RoadMapStepProps extends React.PropsWithChildren {
  idx: number;
  length: number;
  partType: string;
}

const RoadMapStep: React.FC<RoadMapStepProps> = ({
  idx,
  length,
  partType,
  children,
}) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center">
      {getPartIcon(partType)}
      {idx < length - 2 && (
        <div className="my-1 min-h-6 w-px flex-1 bg-gray-300" />
      )}
    </div>
    {children}
  </div>
);

export default StepSummary;
